import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import apiClient from '@/lib/api';

const DIMENSION_COLORS = {
  reliability: { bar: 'bg-blue-500' },
  quality: { bar: 'bg-emerald-500' },
  collaboration: { bar: 'bg-purple-500' },
  professionalism: { bar: 'bg-amber-500' },
  discipline: { bar: 'bg-red-500' },
};

const MemberDetailModal = ({ member, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!member?.ainaa_id) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/admin/members/${member.ainaa_id}/detail`);
        setDetail(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetch();
  }, [member?.ainaa_id]);

  if (!member) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="member-detail-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white border-2 border-[#0F1B2D] w-full max-w-2xl max-h-[85vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-[#0F1B2D] px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F3EE]">{member.name}</h3>
              <span className="text-[11px] font-mono text-[#C4A35A]">{member.ainaa_id}</span>
            </div>
            <button onClick={onClose} className="text-[#9B9589] hover:text-white transition-colors" data-testid="close-member-modal"><X size={18} /></button>
          </div>

          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-[9px] text-[#9B9589] font-bold uppercase">Email</p><p className="text-sm text-[#0F1B2D]">{member.email}</p></div>
              <div><p className="text-[9px] text-[#9B9589] font-bold uppercase">Profession</p><p className="text-sm text-[#0F1B2D]">{member.profession}</p></div>
              {member.phone && <div><p className="text-[9px] text-[#9B9589] font-bold uppercase">Phone</p><p className="text-sm text-[#0F1B2D]">{member.phone}</p></div>}
              {member.country && <div><p className="text-[9px] text-[#9B9589] font-bold uppercase">Country</p><p className="text-sm text-[#0F1B2D]">{member.country}</p></div>}
              {member.field && <div><p className="text-[9px] text-[#9B9589] font-bold uppercase">Field</p><p className="text-sm text-[#0F1B2D] capitalize">{member.field?.replace(/_/g, ' ')}</p></div>}
              <div><p className="text-[9px] text-[#9B9589] font-bold uppercase">App ID</p><p className="text-sm font-mono text-[#9B9589]">{member.application_id}</p></div>
            </div>

            {loading ? (
              <div className="text-center py-6 text-[#9B9589] text-xs">Loading trust score...</div>
            ) : detail?.trust_score ? (
              <div className="border-2 border-[#D8D3CB] p-5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589]">Trust Score</h4>
                  <span className="font-mono text-3xl font-bold text-[#0F1B2D]" style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="modal-trust-score">
                    {detail.trust_score.overall_score}
                    <span className="text-xs text-[#9B9589] font-normal">/1000</span>
                  </span>
                </div>
                {detail.trust_score.dimensions && Object.entries(detail.trust_score.dimensions).map(([key, value]) => {
                  const dc = DIMENSION_COLORS[key] || DIMENSION_COLORS.reliability;
                  return (
                    <div key={key} className="mb-2">
                      <div className="flex justify-between mb-0.5">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-[#4A4640]">{key}</span>
                        <span className="text-[10px] font-mono font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{value}/200</span>
                      </div>
                      <div className="h-2 bg-[#EDECEA]"><div className={`h-full ${dc.bar}`} style={{ width: `${Math.min((value / 200) * 100, 100)}%` }} /></div>
                    </div>
                  );
                })}
                {detail.trust_score.last_calculated && (
                  <p className="text-[9px] text-[#9B9589] mt-2">Last calculated: {new Date(detail.trust_score.last_calculated).toLocaleString()}</p>
                )}
              </div>
            ) : (
              <div className="border border-[#D8D3CB] p-4 text-center"><p className="text-xs text-[#9B9589]">Trust score not yet calculated</p></div>
            )}

            {detail?.endorsements?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589] mb-2">Endorsements ({detail.endorsements.length})</h4>
                <div className="space-y-2">
                  {detail.endorsements.slice(0, 5).map(e => (
                    <div key={e.id} className="bg-emerald-50 border border-emerald-200 p-2.5">
                      <span className="text-xs font-bold text-emerald-800">{e.endorser_name}</span>
                      <span className="text-[10px] text-emerald-600 ml-2">{e.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail?.activities?.length > 0 && (
              <div>
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589] mb-2">Recent Activity ({detail.activities.length})</h4>
                <div className="border border-[#D8D3CB] divide-y divide-[#F0EDE8] max-h-48 overflow-y-auto">
                  {detail.activities.slice(0, 20).map((act, i) => (
                    <div key={i} className="px-3 py-2 flex items-center gap-3 text-xs">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 flex-shrink-0">{act.platform}</span>
                      <span className="text-[#0F1B2D] flex-1 truncate">{act.description || act.action}</span>
                      <span className="text-[#9B9589] flex-shrink-0">{new Date(act.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MemberDetailModal;
